import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Container, Typography, Button, IconButton, Stack, Chip, useTheme,
  Snackbar, Alert,
} from '@mui/material';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import Grid from '@mui/material/Grid';
import { motion, AnimatePresence, useScroll, useTransform, useMotionValue, useSpring } from 'motion/react';
import { content } from '../../data/content';
import { useAnimationVariants } from '../../hooks/useAnimationVariants';
import { useMagneticButton } from '../../hooks/useMagneticButton';

const MotionBox = motion.create(Box);


const PARTICLES = [
  { top: '18%', left: '10%',  size: 5, color: '#818CF8', delay: 0,    anim: 'particle-float-1', dur: '7s'  },
  { top: '72%', left: '6%',   size: 4, color: '#a78bfa', delay: 1.2,  anim: 'particle-float-2', dur: '9s'  },
  { top: '35%', left: '88%',  size: 6, color: '#34d399', delay: 0.5,  anim: 'particle-float-3', dur: '11s' },
  { top: '80%', left: '82%',  size: 4, color: '#818CF8', delay: 2,    anim: 'particle-float-1', dur: '8s'  },
  { top: '55%', left: '92%',  size: 3, color: '#a78bfa', delay: 0.8,  anim: 'particle-float-2', dur: '13s' },
  { top: '10%', left: '55%',  size: 4, color: '#34d399', delay: 3,    anim: 'particle-float-3', dur: '10s' },
  { top: '90%', left: '40%',  size: 5, color: '#818CF8', delay: 1.5,  anim: 'particle-float-1', dur: '12s' },
  { top: '25%', left: '75%',  size: 3, color: '#a78bfa', delay: 2.5,  anim: 'particle-float-2', dur: '8s'  },
];

const TypewriterBadge: React.FC<{ roles: string[] }> = ({ roles }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % roles.length), 3200);
    return () => clearInterval(t);
  }, [roles.length]);
  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={idx}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.38, ease: [0.16, 1, 0.3, 1] }}
        style={{ display: 'inline-block' }}
      >
        {roles[idx]}
      </motion.span>
    </AnimatePresence>
  );
};

// Word-reveal headline component
const WordReveal: React.FC<{ text: string; gradient?: boolean; delay?: number }> = ({ text, gradient, delay = 0 }) => {
  const theme = useTheme();
  const words = text.split(' ');
  return (
    <motion.span
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: 0.065, delayChildren: delay } },
      }}
      initial="hidden"
      animate="visible"
      style={{ display: 'inline' }}
    >
      {words.map((word, i) => (
        <span key={i} style={{ overflow: 'hidden', display: 'inline-block', verticalAlign: 'bottom' }}>
          <motion.span
            variants={{
              hidden:  { opacity: 0, y: '110%', rotateX: -20 },
              visible: { opacity: 1, y: '0%',   rotateX: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
            }}
            style={{
              display: 'inline-block',
              ...(gradient ? {
                // backgroundImage, not the `background` shorthand: on theme change React would rewrite
                // the shorthand, resetting background-clip:text and turning the words into color blocks.
                backgroundImage: `linear-gradient(110deg, ${theme.palette.primary.main} 0%, #a78bfa 30%, #34d399 55%, #a78bfa 80%, ${theme.palette.primary.main} 100%)`,
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                animation: 'gradient-pan 6s linear infinite',
              } : {}),
            }}
          >
            {word}{i < words.length - 1 ? ' ' : ''}
          </motion.span>
        </span>
      ))}
    </motion.span>
  );
};

// 3D tilt for photo
const useTilt = (maxRot = 10) => {
  const ref = useRef<HTMLDivElement>(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [maxRot, -maxRot]), { stiffness: 300, damping: 30 });
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-maxRot, maxRot]), { stiffness: 300, damping: 30 });
  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };
  return { ref, rotX, rotY, onMove, onLeave };
};

const Hero: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { headline, badges, description, buttons, socialLinks, badges2, image, roles } = content.hero;
  const { stagger, fadeUp, slideRight } = useAnimationVariants();

  const sectionRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end start'] });
  const orb1Y   = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const orb2Y   = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -40]);
  const scrollOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);

  const magnetic = useMagneticButton({ strength: 0.3 });
  const tilt = useTilt(8);

  const headlineParts = headline.split('\n');

  const [resumeSnack, setResumeSnack] = useState(false);

  const handleResumeDownload = async () => {
    try {
      const res = await fetch('/resume.pdf', { method: 'HEAD' });
      if (res.ok) {
        const a = document.createElement('a');
        a.href = '/resume.pdf';
        a.download = 'Sajjad_Khalid_Resume.pdf';
        a.click();
      } else {
        setResumeSnack(true);
      }
    } catch {
      setResumeSnack(true);
    }
  };

  const scrollToProjects = () => {
    document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box
      id="home"
      ref={sectionRef}
      sx={{
        pt: { xs: '110px', md: '152px' },
        pb: { xs: '80px', md: '110px' },
        bgcolor: 'background.default',
        position: 'relative',
        overflow: 'hidden',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {/* Dot grid */}
      <Box className="dot-grid" sx={{ position: 'absolute', inset: 0, opacity: isDark ? 1 : 0.4, zIndex: 0, pointerEvents: 'none' }} />

      {/* Orb 1 — top right, slow upward parallax */}
      <MotionBox style={{ y: orb1Y }} sx={{ position: 'absolute', top: '-15%', right: '-8%', width: { xs: '70%', md: '45%' }, paddingBottom: { xs: '70%', md: '45%' }, borderRadius: '50%', background: `radial-gradient(circle, ${theme.palette.primary.main}28 0%, transparent 70%)`, animation: 'float-orb-deep 12s ease-in-out infinite', pointerEvents: 'none', filter: 'blur(60px)', zIndex: 0 }} />

      {/* Orb 2 — bottom left, slow downward parallax */}
      <MotionBox style={{ y: orb2Y }} sx={{ position: 'absolute', bottom: '-12%', left: '-6%', width: { xs: '55%', md: '38%' }, paddingBottom: { xs: '55%', md: '38%' }, borderRadius: '50%', background: 'radial-gradient(circle, rgba(52,211,153,0.2) 0%, transparent 70%)', animation: 'float-orb-1 11s ease-in-out infinite 2s', pointerEvents: 'none', filter: 'blur(60px)', zIndex: 0 }} />

      {/* Floating particles */}
      {PARTICLES.map((p, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            top: p.top,
            left: p.left,
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            bgcolor: p.color,
            opacity: 0.3,
            animation: `${p.anim} ${p.dur} ease-in-out ${p.delay}s infinite`,
            pointerEvents: 'none',
            zIndex: 0,
            display: { xs: 'none', md: 'block' },
          }}
        />
      ))}

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <MotionBox style={{ y: contentY }}>
          <Grid container spacing={{ xs: 6, md: 10 }} alignItems="center">

            {/* ── Left: Text ── */}
            <Grid size={{ xs: 12, md: 7 }}>
              <MotionBox variants={stagger} initial="hidden" animate="visible">

                {/* Role badge */}
                <MotionBox variants={fadeUp}>
                  <Box sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 1,
                    px: 1.75, py: 0.75, borderRadius: '20px',
                    border: `1px solid ${theme.palette.primary.main}44`,
                    bgcolor: `${theme.palette.primary.main}0F`, mb: 3.5,
                  }}>
                    <Box sx={{
                      width: 7, height: 7, borderRadius: '50%',
                      bgcolor: 'primary.main',
                      boxShadow: `0 0 10px ${theme.palette.primary.main}`,
                      animation: 'pulse-ring 2.5s ease-out infinite',
                      position: 'relative', flexShrink: 0,
                    }} />
                    <Typography component="span" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6875rem', letterSpacing: '0.1em', color: 'primary.main', fontWeight: 500 }}>
                      <TypewriterBadge roles={roles} />
                    </Typography>
                  </Box>
                </MotionBox>

                {/* Headline — word by word reveal */}
                <MotionBox variants={fadeUp} sx={{ mb: 3 }}>
                  <Typography variant="h1" component="h1" sx={{ fontWeight: 700, lineHeight: 1.0, letterSpacing: '-0.05em' }}>
                    <WordReveal text={headlineParts[0]} delay={0.15} />
                    <br />
                    <WordReveal text={headlineParts[1]} gradient delay={0.35} />
                  </Typography>
                </MotionBox>

                {/* Tech badges — glassmorphic chips */}
                <MotionBox variants={fadeUp}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 3.5, gap: 0.75 }}>
                    {badges.map((badge) => (
                      <Box
                        key={badge}
                        component="span"
                        sx={{
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                          px: 1.25, py: 0.4,
                          borderRadius: '6px',
                          border: `1px solid ${theme.palette.divider}`,
                          backdropFilter: 'blur(8px)',
                          bgcolor: isDark ? 'rgba(17,24,39,0.6)' : 'rgba(255,255,255,0.6)',
                          display: 'inline-flex', alignItems: 'center',
                        }}
                      >
                        {badge}
                      </Box>
                    ))}
                  </Stack>
                </MotionBox>

                {/* Description */}
                <MotionBox variants={fadeUp}>
                  <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 520, mb: 4.5, lineHeight: 1.85, fontSize: '1.05rem' }}>
                    {description}
                  </Typography>
                </MotionBox>

                {/* CTAs */}
                <MotionBox variants={fadeUp}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 5 }}>
                    {/* Magnetic glow button */}
                    <Box
                      ref={magnetic.ref}
                      onMouseMove={magnetic.handleMouseMove}
                      onMouseLeave={magnetic.handleMouseLeave}
                      component={motion.div}
                      style={{ x: magnetic.x, y: magnetic.y, display: 'inline-block' }}
                    >
                      <Button
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        onClick={scrollToProjects}
                        sx={{
                          px: 3.5, py: 1.5, fontSize: '0.9375rem', fontWeight: 600,
                          background: `linear-gradient(135deg, ${theme.palette.primary.main}, #a78bfa)`,
                          boxShadow: `0 0 32px ${theme.palette.primary.main}50`,
                          border: 'none',
                          '&:hover': {
                            background: `linear-gradient(135deg, #a78bfa, ${theme.palette.primary.main})`,
                            boxShadow: `0 0 48px ${theme.palette.primary.main}70`,
                            transform: 'translateY(-3px)',
                          },
                        }}
                      >
                        {buttons.primary}
                      </Button>
                    </Box>

                    <Button
                      variant="outlined"
                      size="large"
                      onClick={handleResumeDownload}
                      startIcon={<FileDownloadOutlinedIcon />}
                      className="btn-glow"
                      sx={{
                        px: 3.5, py: 1.5, fontSize: '0.9375rem',
                        color: 'text.primary',
                        bgcolor: 'transparent',
                      }}
                    >
                      {buttons.secondary}
                    </Button>
                  </Stack>
                </MotionBox>

                {/* Social icons */}
                <MotionBox variants={fadeUp}>
                  <Stack direction="row" spacing={1.5}>
                    {[
                      { href: socialLinks.github,            icon: <GitHubIcon fontSize="small" />,   label: 'GitHub' },
                      { href: socialLinks.linkedin,          icon: <LinkedInIcon fontSize="small" />, label: 'LinkedIn' },
                      { href: `mailto:${socialLinks.email}`, icon: <EmailIcon fontSize="small" />,   label: 'Email' },
                    ].map(({ href, icon, label }) => (
                      <motion.div key={label} whileHover={{ scale: 1.12, y: -2 }} whileTap={{ scale: 0.95 }}>
                        <IconButton
                          href={href}
                          target={label !== 'Email' ? '_blank' : undefined}
                          rel={label !== 'Email' ? 'noopener noreferrer' : undefined}
                          aria-label={label}
                          size="medium"
                          sx={{
                            color: 'text.secondary',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '10px',
                            width: 42, height: 42,
                            transition: 'all 220ms ease',
                            '&:hover': {
                              color: 'primary.main',
                              borderColor: 'primary.main',
                              bgcolor: `${theme.palette.primary.main}10`,
                              boxShadow: `0 0 20px ${theme.palette.primary.main}35`,
                            },
                          }}
                        >
                          {icon}
                        </IconButton>
                      </motion.div>
                    ))}
                  </Stack>
                </MotionBox>

              </MotionBox>
            </Grid>

            {/* ── Right: Photo with 3D tilt ── */}
            <Grid size={{ xs: 12, md: 5 }} sx={{ display: 'flex', justifyContent: 'center' }}>
              <MotionBox
                variants={slideRight}
                initial="hidden"
                animate="visible"
                sx={{ position: 'relative', width: { xs: 280, sm: 340, md: 370 }, perspective: '1200px' }}
              >
                {/* 3D tilt container */}
                <Box
                  ref={tilt.ref}
                  onMouseMove={tilt.onMove}
                  onMouseLeave={tilt.onLeave}
                  component={motion.div}
                  style={{ rotateX: tilt.rotX, rotateY: tilt.rotY, transformStyle: 'preserve-3d' } as any}
                  sx={{ position: 'relative' }}
                >
                  {/* Rotating glow ring */}
                  <Box sx={{
                    position: 'absolute',
                    inset: -4,
                    borderRadius: '16px',
                    background: `conic-gradient(from 0deg, ${theme.palette.primary.main}80, #a78bfa80, #34d39960, ${theme.palette.primary.main}80)`,
                    animation: 'gradient-rotate 8s linear infinite',
                    zIndex: 0,
                    filter: 'blur(2px)',
                    transform: 'translateZ(-10px)',
                  }} />

                  {/* Photo frame */}
                  <Box sx={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '4/5',
                    borderRadius: '14px',
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    zIndex: 1,
                    boxShadow: `0 32px 80px rgba(0,0,0,0.5), 0 0 0 1px ${theme.palette.divider}`,
                    transform: 'translateZ(20px)',
                  }}>
                    <Box
                      component="img"
                      src={image}
                      alt="Sajjad Khalid — React Native Engineer specializing in cross-platform iOS and Android development"
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block' }}
                    />
                  </Box>

                  {/* Floating code card — translateZ for 3D depth */}
                  <MotionBox
                    initial={{ opacity: 0, scale: 0.85, x: 20 }}
                    animate={{ opacity: 1, scale: 1, x: 0 }}
                    transition={{ delay: 0.7, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    sx={{
                      position: 'absolute',
                      top: { xs: -20, md: -28 },
                      left: { xs: -16, md: -48 },
                      zIndex: 3,
                      bgcolor: isDark ? 'rgba(13,17,23,0.95)' : 'rgba(255,255,255,0.95)',
                      backdropFilter: 'blur(20px)',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: '12px',
                      p: 1.75,
                      maxWidth: 220,
                      boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
                      transform: 'translateZ(40px)',
                    }}
                  >
                    <Typography component="pre" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6rem', lineHeight: 1.6, color: 'text.secondary', m: 0, overflow: 'hidden', whiteSpace: 'pre-wrap', '& .kw': { color: theme.palette.primary.main }, '& .str': { color: '#34d399' }, '& .tag': { color: '#f472b6' } }}>
                      <span className="kw">const</span>{' App = () => (\n  '}
                      <span className="tag">{'<NavigationContainer>'}</span>
                      {'\n    '}
                      <span className="tag">{'<Stack.Screen'}</span>
                      {'\n      name='}
                      <span className="str">"Home"</span>
                      {'\n      component={Home}\n    />\n  '}
                      <span className="tag">{'</NavigationContainer>'}</span>
                      {'\n);'}
                    </Typography>
                  </MotionBox>

                  {/* Badge: Years */}
                  <MotionBox
                    initial={{ opacity: 0, scale: 0.7 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1.0, duration: 0.4, type: 'spring', stiffness: 280 }}
                    sx={{ position: 'absolute', bottom: { xs: 16, md: 24 }, right: { xs: -16, md: -36 }, zIndex: 3, transform: 'translateZ(60px)' }}
                  >
                    <Chip label={badges2[0].label} size="small" variant="outlined" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6875rem', backdropFilter: 'blur(16px)', bgcolor: isDark ? 'rgba(13,17,23,0.9)' : 'rgba(255,255,255,0.9)', borderColor: 'primary.main', color: 'primary.main', boxShadow: `0 4px 20px rgba(0,0,0,0.3), 0 0 16px ${theme.palette.primary.main}35`, height: 34, px: 0.5 }} />
                  </MotionBox>

                  {/* Badge: Apps shipped */}
                  <MotionBox
                    initial={{ opacity: 0, scale: 0.7 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1.2, duration: 0.4, type: 'spring', stiffness: 280 }}
                    sx={{ position: 'absolute', top: { xs: 60, md: 80 }, right: { xs: -16, md: -40 }, zIndex: 3, transform: 'translateZ(60px)' }}
                  >
                    <Chip label={badges2[1].label} size="small" variant="outlined" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6875rem', backdropFilter: 'blur(16px)', bgcolor: isDark ? 'rgba(13,17,23,0.9)' : 'rgba(255,255,255,0.9)', borderColor: '#34d399', color: '#34d399', boxShadow: '0 4px 20px rgba(0,0,0,0.3)', height: 34, px: 0.5 }} />
                  </MotionBox>
                </Box>
              </MotionBox>
            </Grid>

          </Grid>

          {/* Scroll indicator */}
          <MotionBox
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6, duration: 0.5 }}
            style={{ opacity: scrollOpacity }}
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, mt: { xs: 6, md: 8 } }}
          >
            <Typography variant="caption" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.625rem', letterSpacing: '0.12em', color: 'text.secondary', opacity: 0.5 }}>
              SCROLL
            </Typography>
            <Box sx={{ width: 1.5, height: 44, bgcolor: 'divider', borderRadius: '1px', position: 'relative', overflow: 'hidden', '&::after': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '50%', bgcolor: 'primary.main', animation: 'float-orb-3 1.8s ease-in-out infinite', borderRadius: '1px' } }} />
          </MotionBox>
        </MotionBox>
      </Container>

      <Snackbar
        open={resumeSnack}
        autoHideDuration={5000}
        onClose={() => setResumeSnack(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setResumeSnack(false)}
          severity="info"
          variant="filled"
          sx={{ borderRadius: '10px', fontFamily: '"Space Grotesk", sans-serif' }}
        >
          Resume PDF not uploaded yet — email me at sajjadkhalid753@gmail.com
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Hero;
